import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AMDashboard.css';
import { getToken } from '../../../../../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const AMDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState({
    totalValue: 0,
    totalCount: 0,
    totalCountFiltered: 0,
    oldestPrioridadeDate: null,
    formattedOldestPrioridadeDate: 'N/A',
    oldestPrioridadeOrcamento: null,
    breakdown: {
      prioridade: { value: 0, count: 0 },
      regular: { value: 0, count: 0 }
    },
    filteredBreakdown: {
      prioridade: { count: 0 },
      regular: { count: 0 }
    },
    rclTotals: {
      totalGeral: 0,
      totalPrioritarios: 0,
      totalRegulares: 0,
      counts: {
        prioritarios: 0,
        regulares: 0
      }
    }
  });
  const [annualRCLValue, setAnnualRCLValue] = useState('');
  const [contaRCL109Value, setContaRCL109Value] = useState('');
  const [projection, setProjection] = useState(null);
  const [loadingProjection, setLoadingProjection] = useState(false);
  const [activeTab, setActiveTab] = useState('statistics');
  const [duplicates, setDuplicates] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [duplicateStats, setDuplicateStats] = useState({ totalGroups: 0, totalRecords: 0 });

  useEffect(() => {
    fetchStatistics();
  }, []);

  useEffect(() => {
    if (activeTab === 'duplicates' && duplicates.length === 0) {
      fetchDuplicates();
    }
  }, [activeTab]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;
      
      const response = await fetch(`${API_URL}/precatorioAM/rcl/statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar estatísticas');
      }

      const data = await response.json();
      
      // Format the data
      setStatistics({
        totalValue: data.totalValue || 0,
        totalCount: data.totalCount || 0,
        totalCountFiltered: data.totalCountFiltered || 0,
        oldestPrioridadeDate: data.oldestPrioridadeDate,
        formattedOldestPrioridadeDate: data.oldestPrioridadeDate ? formatDate(data.oldestPrioridadeDate) : 'N/A',
        oldestPrioridadeOrcamento: data.oldestPrioridadeOrcamento || null,
        breakdown: data.breakdown || {
          prioridade: { value: 0, count: 0 },
          regular: { value: 0, count: 0 }
        },
        filteredBreakdown: data.filteredBreakdown || {
          prioridade: { count: 0 },
          regular: { count: 0 }
        },
        rclTotals: data.rclTotals || {
          totalGeral: 0,
          totalPrioritarios: 0,
          totalRegulares: 0,
          counts: {
            prioritarios: 0,
            regulares: 0
          }
        }
      });
      
    } catch (err) {
      console.error('Erro:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Handle different date formats
    if (dateString.includes('/')) {
      // Already in DD/MM/YYYY format
      return dateString;
    }
    
    // Convert from ISO or other formats
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const handleBackClick = () => {
    navigate('/admin/tabela-rcl');
  };

  const handleCalculateProjection = async () => {
    if (!annualRCLValue || parseFloat(annualRCLValue.replace(/\./g, '').replace(',', '.')) <= 0) {
      alert('Por favor, insira um valor anual RCL válido');
      return;
    }

    try {
      setLoadingProjection(true);
      setError(null);
      
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;
      
      const response = await fetch(`${API_URL}/precatorioAM/rcl/projection`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          annualRCLValue: parseFloat(annualRCLValue.replace(/\./g, '').replace(',', '.')),
          contaRCL109: parseFloat(contaRCL109Value.replace(/\./g, '').replace(',', '.')) || 0
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao calcular projeção');
      }

      const data = await response.json();
      setProjection(data);
      
    } catch (err) {
      console.error('Erro:', err);
      setError(err.message);
    } finally {
      setLoadingProjection(false);
    }
  };

  const handleRCLValueChange = (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except comma and dot
    value = value.replace(/[^0-9,]/g, '');
    
    // Replace comma with dot for parsing
    const numericValue = value.replace(',', '.');
    
    // Update the state with the numeric value
    if (value === '' || !isNaN(parseFloat(numericValue))) {
      setAnnualRCLValue(value);
    }
  };

  const formatDisplayValue = (value) => {
    if (!value) return '';
    
    // Convert to number
    const numValue = parseFloat(value.replace(',', '.'));
    if (isNaN(numValue)) return value;
    
    // Format as Brazilian currency
    return numValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleRCLInputFocus = (e) => {
    // Remove formatting on focus for easier editing
    const numericValue = annualRCLValue.replace(/\./g, '').replace(',', '.');
    setAnnualRCLValue(numericValue);
  };

  const handleRCLInputBlur = (e) => {
    // Apply formatting on blur
    if (annualRCLValue) {
      const formatted = formatDisplayValue(annualRCLValue);
      setAnnualRCLValue(formatted);
    }
  };

  const handleContaRCL109Change = (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except comma and dot
    value = value.replace(/[^0-9,]/g, '');
    
    // Replace comma with dot for parsing
    const numericValue = value.replace(',', '.');
    
    // Update the state with the numeric value
    if (value === '' || !isNaN(parseFloat(numericValue))) {
      setContaRCL109Value(value);
    }
  };

  const handleContaRCL109Focus = (e) => {
    // Remove formatting on focus for easier editing
    const numericValue = contaRCL109Value.replace(/\./g, '').replace(',', '.');
    setContaRCL109Value(numericValue);
  };

  const handleContaRCL109Blur = (e) => {
    // Apply formatting on blur
    if (contaRCL109Value) {
      const formatted = formatDisplayValue(contaRCL109Value);
      setContaRCL109Value(formatted);
    }
  };

  const fetchDuplicates = async () => {
    try {
      setLoadingDuplicates(true);
      setError(null);
      
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;
      
      const response = await fetch(`${API_URL}/precatorioAM/duplicates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar duplicados');
      }

      const data = await response.json();
      setDuplicates(data.duplicates || []);
      setDuplicateStats({
        totalGroups: data.totalGroups || 0,
        totalRecords: data.totalRecords || 0
      });
      
    } catch (err) {
      console.error('Erro:', err);
      setError(err.message);
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Add header with border
    doc.setFillColor(52, 152, 219);
    doc.rect(0, 0, pageWidth, 18, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Relatório - Precatórios RCL Amazonas', pageWidth / 2, 10, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Data de Geração: ${new Date().toLocaleString('pt-BR')}`, pageWidth / 2, 14, { align: 'center' });
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    
    // Add RCL Totals section
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Totais RCL', 14, 25);
    
    // Draw line under title
    doc.setLineWidth(0.5);
    doc.line(14, 27, pageWidth - 14, 27);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    let yPos = 33;
    
    // Create statistics box
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    
    // Total Geral
    doc.rect(14, yPos - 5, pageWidth - 28, 15);
    doc.text('Total Geral:', 16, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(formatCurrency(statistics.rclTotals.totalGeral), pageWidth - 16, yPos, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.text('Regulares (até 2025) + Todos Prioritários', 16, yPos + 5);
    doc.setFontSize(10);
    
    // Total Prioritários
    yPos += 17;
    doc.rect(14, yPos - 5, pageWidth - 28, 15);
    doc.text('Total Prioritários:', 16, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(formatCurrency(statistics.rclTotals.totalPrioritarios), pageWidth - 16, yPos, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.text(`${statistics.rclTotals.counts.prioritarios} precatórios - todos independente da data`, 16, yPos + 5);
    doc.setFontSize(10);
    
    // Total Regulares
    yPos += 17;
    doc.rect(14, yPos - 5, pageWidth - 28, 15);
    doc.text('Total Regulares:', 16, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(formatCurrency(statistics.rclTotals.totalRegulares), pageWidth - 16, yPos, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.text(`${statistics.rclTotals.counts.regulares} precatórios (até 2025)`, 16, yPos + 5);
    doc.setFontSize(10);
    
    // Add statistics details section
    yPos += 18;
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Estatísticas Detalhadas', 14, yPos);
    
    // Draw line under title
    doc.setLineWidth(0.5);
    doc.line(14, yPos + 2, pageWidth - 14, yPos + 2);
    
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    
    // Total Count
    doc.rect(14, yPos - 5, pageWidth - 28, 10);
    doc.text('Quantidade Total:', 16, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(statistics.totalCount.toString(), pageWidth - 80, yPos, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.text(`(Prioritários: ${statistics.breakdown.prioridade.count} | Regulares: ${statistics.breakdown.regular.count})`, pageWidth - 16, yPos, { align: 'right' });
    doc.setFontSize(10);
    
    // Oldest Orçamento
    yPos += 12;
    doc.rect(14, yPos - 5, pageWidth - 28, 10);
    doc.text('Orçamento Prioritário Mais Antigo:', 16, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(statistics.oldestPrioridadeOrcamento ? statistics.oldestPrioridadeOrcamento.toString() : 'N/A', pageWidth - 16, yPos, { align: 'right' });
    doc.setFont(undefined, 'normal');
    
    // Total Count Filtered
    yPos += 12;
    doc.rect(14, yPos - 5, pageWidth - 28, 10);
    doc.text('Total Precatório (até 2025):', 16, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(statistics.totalCountFiltered.toString(), pageWidth - 80, yPos, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.text(`(Prioritários: ${statistics.filteredBreakdown.prioridade.count} | Regulares: ${statistics.filteredBreakdown.regular.count})`, pageWidth - 16, yPos, { align: 'right' });
    doc.setFontSize(10);
    
    // Add projection section if available
    if (projection) {
      yPos += 12;
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('Projeção de Pagamento RCL', 14, yPos);
      
      // Draw line under title
      doc.setLineWidth(0.5);
      doc.line(14, yPos + 2, pageWidth - 14, yPos + 2);
      
      yPos += 8;
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      
      // Create projection summary box
      const summaryData = [
        ['Valor Anual RCL:', formatCurrency(projection.annualRCLValue)],
        ['Valor Anual RCL(2025)(5 meses):', formatCurrency((projection.annualRCLValue)*(5/12))],
        ...(projection.contaRCL109 > 0 ? [['Conta RCL EC-109(7 meses):', formatCurrency((projection.contaRCL109)*(7/12))]] : []),
        ['Valor Total RCL(2025)(EC-109 + PEC 66)(Valor = Somatória * Cálculo):', formatCurrency(((projection.contaRCL109 * (7/12) || 0) + ((5/12) * projection.annualRCLValue)) * 1.33)],
        ['Total de Precatórios:', projection.totalPrecatorios.toString()],
        ['Valor Total:', formatCurrency(projection.totalValue)],
        ['Anos para Pagar Todos:', projection.yearsToPayAll.toString()],
        ['Último Ano de Pagamento:', projection.lastPaymentYear.toString()]
      ];
      
      summaryData.forEach((item, index) => {
        // Check if we need a new page before drawing
        if (yPos + 10 > 210) {
          doc.addPage();
          yPos = 10;
        }
        
        doc.rect(14, yPos - 5, pageWidth - 28, 10);
        doc.text(item[0], 16, yPos);
        doc.setFont(undefined, 'bold');
        doc.text(item[1], pageWidth - 16, yPos, { align: 'right' });
        doc.setFont(undefined, 'normal');
        yPos += 12;
      });
      
      // Add phase statistics if available
      if (projection.phase1Stats && projection.phase2Stats) {
        yPos += 0;
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Estatísticas por Etapa', 14, yPos);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        
        yPos += 2;
        
        const phaseTableColumns = ['Etapa', 'Quantidade', 'Valor'];
        const phaseTableRows = [
          ['(até 2025 + Prioritários)', 
           projection.phase1Stats.count.toString(), 
           formatCurrency(projection.phase1Stats.value)],
          ['(Orçamento > 2025)', 
           projection.phase2Stats.count.toString(), 
           formatCurrency(projection.phase2Stats.value)]
        ];
        
        autoTable(doc, {
          head: [phaseTableColumns],
          body: phaseTableRows,
          startY: yPos,
          styles: { 
            fontSize: 8,
            cellPadding: 3,
            lineColor: [200, 200, 200],
            lineWidth: 0.3
          },
          headStyles: { 
            fillColor: [52, 152, 219],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center'
          },
          columnStyles: {
            0: { cellWidth: 100 },
            1: { halign: 'center', cellWidth: 30 },
            2: { halign: 'right', cellWidth: 50 }
          },
          margin: { left: 14, right: 14 }
        });
        
        yPos = doc.lastAutoTable.finalY;
      }
      
      // Add projection table with better formatting
      yPos += 5;
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Projeção Detalhada por Ano', 14, yPos);
      doc.setFont(undefined, 'normal');
      
      yPos += 2;
      
      const tableColumns = [
        'Ano',
        'Valor Pago',
        'Caixa Restante',
        'Total Pago',
        'Valor Restante',
        'Precatórios Restantes',
        'Qtd Precatórios Pgos'
      ];
      
      const tableRows = [];
      projection.projection.forEach((year, index) => {
        // Add phase transition row if needed
        if (year.isPhaseTransition) {
          tableRows.push([{
            content: 'Início - Orçamento 2026+',
            colSpan: 7,
            styles: { halign: 'center', fontStyle: 'bold', fillColor: [240, 240, 240] }
          }]);
        }
        
        // Add orçamento transition row if needed
        if (year.orcamentoTransition && !year.orcamentoTransition.isPhase2Start) {
          const transitionText = year.orcamentoTransition.from <= 2025 ? 
            `Fim Orçamento <= ${year.orcamentoTransition.from}` : 
            `Início Orçamento ${year.orcamentoTransition.to}`;
          tableRows.push([{
            content: transitionText,
            colSpan: 7,
            styles: { halign: 'center', fontStyle: 'bold', fillColor: [232, 244, 248], textColor: [44, 90, 160] }
          }]);
        }
        
        // Add regular data row
        tableRows.push([
          year.year.toString(),
          formatCurrency(year.paidValue),
          formatCurrency(year.remainingBudget),
          formatCurrency(year.totalPaidSoFar),
          formatCurrency(projection.totalValue - year.totalPaidSoFar),
          year.remainingCount.toString(),
          year.paidCount.toString()
        ]);
      });
      
      autoTable(doc, {
        head: [tableColumns],
        body: tableRows,
        startY: yPos,
        styles: { 
          fontSize: 7,
          cellPadding: 3,
          lineColor: [200, 200, 200],
          lineWidth: 0.3
        },
        headStyles: { 
          fillColor: [52, 152, 219],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          halign: 'center'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { halign: 'center' }, // Ano
          1: { halign: 'right' },  // Valor Pago
          2: { halign: 'right' },  // Caixa Restante
          3: { halign: 'right' },  // Total Pago
          4: { halign: 'right' },  // Valor Restante
          5: { halign: 'center' }, // Precatórios Restantes
          6: { halign: 'center' }  // Qtd Precatórios Pgos
        },
        margin: { left: 14, right: 14 }
      });
      
      // Add Orçamento Payment Matrix Table
      if (projection.orcamentoPaymentMatrix) {
        yPos = doc.lastAutoTable.finalY + 8;
        
        // Add title
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Orçamentos Contemplados por Ano de Pagamento', 14, yPos);
        doc.setFont(undefined, 'normal');
        yPos += 2;
        
        // Prepare table data
        const orcamentoColumns = ['ANO DE PREVISÃO DO PAGAMENTO'];
        projection.projection.forEach(year => {
          orcamentoColumns.push(year.year.toString());
        });
        
        // Prioridades row
        const prioridadesRow = ['ANO(S) ORÇAMENTÁRIO(S) CONTEMPLADOS - PRIORIDADES'];
        projection.projection.forEach(year => {
          const prioridadeData = projection.orcamentoPaymentMatrix.prioridades[year.year];
          if (prioridadeData) {
            prioridadesRow.push(
              prioridadeData.min === prioridadeData.max 
                ? prioridadeData.min.toString()
                : `${prioridadeData.min}-${prioridadeData.max}`
            );
          } else {
            prioridadesRow.push('-');
          }
        });
        
        // Regulares row
        const regularesRow = ['ANO(S) ORÇAMENTÁRIO(S) CONTEMPLADOS - REGULARES'];
        projection.projection.forEach(year => {
          const regularData = projection.orcamentoPaymentMatrix.regulares[year.year];
          if (regularData) {
            regularesRow.push(
              regularData.min === regularData.max 
                ? regularData.min.toString()
                : `${regularData.min}-${regularData.max}`
            );
          } else {
            regularesRow.push('-');
          }
        });
        
        // Create the table
        autoTable(doc, {
          head: [orcamentoColumns],
          body: [prioridadesRow, regularesRow],
          startY: yPos,
          styles: { 
            fontSize: 7,
            cellPadding: 3,
            lineColor: [200, 200, 200],
            lineWidth: 0.3
          },
          headStyles: { 
            fillColor: [52, 152, 219],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center'
          },
          columnStyles: {
            0: { cellWidth: 80, halign: 'left', fontStyle: 'bold', fillColor: [240, 240, 240] }
          },
          bodyStyles: {
            halign: 'center'
          },
          margin: { left: 14, right: 14 }
        });
      }
    }
    
    // Add footer
    doc.setFontSize(6);
    doc.setTextColor(128, 128, 128);
    doc.text('Documento gerado automaticamente pelo sistema', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    // Save the PDF
    doc.save(`Relatorio_RCL_Amazonas_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportDuplicatesToPDF = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Add header with border
    doc.setFillColor(255, 152, 0);
    doc.rect(0, 0, pageWidth, 18, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont(undefined, 'bold');
    doc.text('Relatório - Duplicados de Precatórios MA', pageWidth / 2, 10, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(`Data de Geração: ${new Date().toLocaleString('pt-BR')}`, pageWidth / 2, 14, { align: 'center' });
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    
    // Add summary section
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('Resumo', 14, 35);
    
    // Draw line under title
    doc.setLineWidth(0.5);
    doc.line(14, 37, pageWidth - 14, 37);
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    let yPos = 47;
    
    // Summary statistics
    doc.rect(14, yPos - 5, pageWidth - 28, 10);
    doc.text('Total de Grupos Duplicados:', 16, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(duplicateStats.totalGroups.toString(), pageWidth - 16, yPos, { align: 'right' });
    doc.setFont(undefined, 'normal');
    
    yPos += 12;
    doc.rect(14, yPos - 5, pageWidth - 28, 10);
    doc.text('Total de Registros Repetidos:', 16, yPos);
    doc.setFont(undefined, 'bold');
    doc.text(duplicateStats.totalRecords.toString(), pageWidth - 16, yPos, { align: 'right' });
    doc.setFont(undefined, 'normal');
    
    // Add duplicates table
    if (duplicates.length > 0) {
      yPos += 20;
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Lista de Duplicados', 14, yPos);
      doc.setFont(undefined, 'normal');
      
      yPos += 8;
      
      const tableColumns = [
        'Ordem',
        'Número Precatório',
        'Natureza',
        'Orçamento',
        'Prioridade',
        'Valor',
        'Ente',
        'Recebimento',
        'Fonte'
      ];
      
      const tableRows = duplicates.map(dup => [
        dup.ordem?.toString() || '',
        dup.numero_precatorio || '',
        dup.natureza || '',
        dup.orcamento || '',
        dup.prioridade || '',
        formatCurrency(dup.valor_atualizado || 0),
        dup.ente || '',
        dup.recebimento || '',
        dup.fonte || ''
      ]);
      
      // Custom styling for groups
      const bodyStyles = [];
      duplicates.forEach((dup, index) => {
        // Check if this is the start of a new group
        const isGroupStart = index === 0 || dup.numero_precatorio !== duplicates[index - 1].numero_precatorio;
        const groupIndex = isGroupStart ? Math.floor(index / 2) : Math.floor((index - 1) / 2);
        const useAlternateColor = groupIndex % 2 === 0;
        
        if (dup.fonte === 'Regular') {
          bodyStyles.push({
            fillColor: useAlternateColor ? [235, 245, 255] : [255, 255, 255],
            textColor: [0, 0, 0]
          });
        } else {
          bodyStyles.push({
            fillColor: useAlternateColor ? [255, 243, 224] : [255, 255, 255],
            textColor: [0, 0, 0]
          });
        }
      });
      
      autoTable(doc, {
        head: [tableColumns],
        body: tableRows,
        startY: yPos,
        styles: { 
          fontSize: 8,
          cellPadding: 2,
          lineColor: [200, 200, 200],
          lineWidth: 0.3
        },
        headStyles: { 
          fillColor: [255, 152, 0],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          halign: 'center'
        },
        columnStyles: {
          0: { halign: 'center' },
          1: { halign: 'left' },
          2: { halign: 'center' },
          3: { halign: 'center' },
          4: { halign: 'center' },
          5: { halign: 'right' },
          6: { halign: 'center' },
          7: { halign: 'center' },
          8: { halign: 'center' }
        },
        tableWidth: 'auto',
        didDrawRow: (data) => {
          // Add thicker border after each group
          const rowIndex = data.row.index;
          const isGroupEnd = rowIndex === duplicates.length - 1 || 
            duplicates[rowIndex].numero_precatorio !== duplicates[rowIndex + 1].numero_precatorio;
          
          if (isGroupEnd) {
            doc.setLineWidth(1);
            doc.setDrawColor(200, 200, 200);
            doc.line(
              data.cell(0).x,
              data.row.y + data.row.height,
              data.cell(8).x + data.cell(8).width,
              data.row.y + data.row.height
            );
            doc.setLineWidth(0.3);
          }
        },
        margin: { left: 14, right: 14 }
      });
    }
    
    // Add footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('Documento gerado automaticamente pelo sistema', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    // Save the PDF
    doc.save(`Relatorio_Duplicados_MA_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (loading) {
    return (
      <div className="am-dashboard-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Carregando estatísticas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="am-dashboard-container">
        <div className="error-state">
          <h2>Erro</h2>
          <p>{error}</p>
          <button className="retry-button" onClick={fetchStatistics}>
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="am-dashboard-container">
      <div className="dashboard-header">
        <button className="back-button" onClick={handleBackClick}>
          ← Voltar
        </button>
        <h1>Precatórios RCL Amazonas</h1>
        <div className="header-actions">
          <button className="export-button" onClick={exportToPDF}>
            📄 Exportar PDF
          </button>
          <button className="refresh-button" onClick={fetchStatistics}>
            Atualizar
          </button>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs-nav">
          <button 
            className={`tab-button ${activeTab === 'statistics' ? 'active' : ''}`}
            onClick={() => setActiveTab('statistics')}
          >
            Estatísticas
          </button>
          <button 
            className={`tab-button ${activeTab === 'duplicates' ? 'active' : ''}`}
            onClick={() => setActiveTab('duplicates')}
          >
            Duplicados
          </button>
        </div>
      </div>

      {activeTab === 'statistics' && (
        <>
      <div className="statistics-grid">
        <div className="stat-card total-value">
          <div className="stat-icon">
            <span>R$</span>
          </div>
          <div className="stat-content">
            <h3>Total Geral</h3>
            <p className="stat-value">{formatCurrency(statistics.rclTotals.totalGeral)}</p>
            <span className="stat-description">Regulares (até 2025) + Todos Prioritários</span>
          </div>
        </div>

        <div className="stat-card total-count">
          <div className="stat-icon">
            <span>R$</span>
          </div>
          <div className="stat-content">
            <h3>Total Prioritários</h3>
            <p className="stat-value">{formatCurrency(statistics.rclTotals.totalPrioritarios)}</p>
            <span className="stat-description">
              Todos os prioritários ({statistics.rclTotals.counts.prioritarios} precatórios)
            </span>
          </div>
        </div>

        <div className="stat-card oldest-date">
          <div className="stat-icon">
            <span>R$</span>
          </div>
          <div className="stat-content">
            <h3>Total Regulares</h3>
            <p className="stat-value">{formatCurrency(statistics.rclTotals.totalRegulares)}</p>
            <span className="stat-description">
              Regulares até 2025 ({statistics.rclTotals.counts.regulares} precatórios)
            </span>
          </div>
        </div>

        <div className="stat-card total-filtered">
          <div className="stat-icon">
            <span>📅</span>
          </div>
          <div className="stat-content">
            <h3>Orçamento Prioritário Mais Antigo</h3>
            <p className="stat-value">{statistics.oldestPrioridadeOrcamento || 'N/A'}</p>
            <span className="stat-description">
              Total de precatórios: {statistics.totalCount}
            </span>
          </div>
        </div>
      </div>

      <div className="rcl-projection-section">
        <h2>Projeção de Pagamento RCL</h2>
        <div className="rcl-input-container">
          <div className="rcl-inputs-row">
            <div className="rcl-input-group">
              <label htmlFor="conta-rcl-109">Conta RCL EC-109:</label>
              <div className="rcl-input-wrapper">
                <span className="currency-prefix">R$</span>
                <input
                  id="conta-rcl-109"
                  type="text"
                  value={contaRCL109Value}
                  onChange={handleContaRCL109Change}
                  onFocus={handleContaRCL109Focus}
                  onBlur={handleContaRCL109Blur}
                  placeholder="0,00"
                  className="rcl-input"
                />
              </div>
            </div>
            <div className="rcl-input-group">
              <label htmlFor="annual-rsl">Valor Anual RCL:</label>
              <div className="rcl-input-wrapper">
                <span className="currency-prefix">R$</span>
                <input
                  id="annual-rsl"
                  type="text"
                  value={annualRCLValue}
                  onChange={handleRCLValueChange}
                  onFocus={handleRCLInputFocus}
                  onBlur={handleRCLInputBlur}
                  placeholder="0,00"
                  className="rcl-input"
                />
              </div>
            </div>
          </div>
          <button 
            className="calculate-button"
            onClick={handleCalculateProjection}
            disabled={loadingProjection}
          >
            {loadingProjection ? 'Calculando...' : 'Calcular Projeção'}
          </button>
        </div>

        {projection && (
          <div className="projection-results">
            <div className="projection-summary">
              <h3>Resumo da Projeção</h3>
              {projection.contaRCL109 > 0 && (
                <p><strong>Conta RCL EC-109 (7 meses):</strong> {formatCurrency((projection.contaRCL109)*(7/12))}</p>
              )}
              <p><strong>Valor Anual RCL (2025)(5 meses):</strong> {formatCurrency((projection.annualRCLValue)*(5/12))}</p>
              <p><strong>Valor Total RCL(2025)(EC-109 + PEC 66)(Valor = Somatória * Cálculo):</strong> {formatCurrency(((projection.contaRCL109 * (7/12) || 0) + ((5/12) * projection.annualRCLValue)) * 1.33)}</p>
              <p><strong>Valor Anual RCL:</strong> {formatCurrency(projection.annualRCLValue)}</p>
              <p><strong>Total de Precatórios:</strong> {projection.totalPrecatorios}</p>
              <p><strong>Valor Total:</strong> {formatCurrency(projection.totalValue)}</p>
              <p><strong>Anos para Pagar Todos:</strong> {projection.yearsToPayAll}</p>
              <p><strong>Último Ano de Pagamento:</strong> {projection.lastPaymentYear}</p>
              {projection.phase1Stats && projection.phase2Stats && (
                <>
                  <hr style={{ margin: '15px 0', borderColor: '#e0e0e0' }} />
                  <p><strong>(Orçamento &lt;= 2025 + Prioritários):</strong></p>
                  <p style={{ marginLeft: '20px' }}>
                    {projection.phase1Stats.count} precatórios - {formatCurrency(projection.phase1Stats.value)}
                  </p>
                  <p><strong>(Orçamento &gt; 2025):</strong></p>
                  <p style={{ marginLeft: '20px' }}>
                    {projection.phase2Stats.count} precatórios - {formatCurrency(projection.phase2Stats.value)}
                  </p>
                </>
              )}
            </div>

            <div className="projection-table-container">
              <h3>Projeção Ano a Ano</h3>
              <table className="projection-table">
                <thead>
                  <tr>
                    <th>Ano</th>
                    <th>Valor Pago</th>
                    <th>Caixa Restante</th>
                    <th>Total Pago Até o Momento</th>
                    <th>Valor Restante a Pagar</th>
                    <th>Precatórios Restantes</th>
                    <th>Qtd Precatórios Pgos</th>
                  </tr>
                </thead>
                <tbody>
                  {projection.projection.map((year, index) => (
                    <React.Fragment key={index}>
                      {year.isPhaseTransition && (
                        <tr className="phase-transition">
                          <td colSpan="8" style={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f0f0f0', padding: '10px' }}>
                            Início - Orçamento 2026+
                          </td>
                        </tr>
                      )}
                      {year.orcamentoTransition && !year.orcamentoTransition.isPhase2Start && (
                        <tr className="orcamento-transition">
                          <td colSpan="8" style={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#e8f4f8', padding: '8px', color: '#2c5aa0' }}>
                            {year.orcamentoTransition.from <= 2025 ? 
                              `Fim Orçamento <= ${year.orcamentoTransition.from}` : 
                              `Início Orçamento ${year.orcamentoTransition.to}`
                            }
                          </td>
                        </tr>
                      )}
                      <tr className={year.paidCount === 0 ? 'no-payment' : ''}>
                        <td>{year.year}</td>
                        <td>{formatCurrency(year.paidValue)}</td>
                        <td>{formatCurrency(year.remainingBudget)}</td>
                        <td>{formatCurrency(year.totalPaidSoFar)}</td>
                        <td>{formatCurrency(projection.totalValue - year.totalPaidSoFar)}</td>
                        <td>{year.remainingCount}</td>
                        <td>{year.paidCount}</td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* New Orçamento Payment Table */}
            {projection.orcamentoPaymentMatrix && (
              <div className="orcamento-payment-table-container" style={{ marginTop: '30px' }}>
                <h3>Orçamentos Contemplados por Ano de Pagamento</h3>
                <table className="projection-table">
                  <thead>
                    <tr>
                      <th style={{ width: '300px' }}>ANO DE PREVISÃO DO PAGAMENTO</th>
                      {projection.projection.map((year, index) => (
                        <th key={index} style={{ minWidth: '100px' }}>{year.year}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>
                        ANO(S) ORÇAMENTÁRIO(S)<br/>CONTEMPLADOS - PRIORIDADES
                      </td>
                      {projection.projection.map((year, index) => {
                        const prioridadeData = projection.orcamentoPaymentMatrix.prioridades[year.year];
                        return (
                          <td key={index} style={{ textAlign: 'center' }}>
                            {prioridadeData ? (
                              prioridadeData.min === prioridadeData.max 
                                ? prioridadeData.min.toString()
                                : `${prioridadeData.min}-${prioridadeData.max}`
                            ) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>
                        ANO(S) ORÇAMENTÁRIO(S)<br/>CONTEMPLADOS - REGULARES
                      </td>
                      {projection.projection.map((year, index) => {
                        const regularData = projection.orcamentoPaymentMatrix.regulares[year.year];
                        return (
                          <td key={index} style={{ textAlign: 'center' }}>
                            {regularData ? (
                              regularData.min === regularData.max 
                                ? regularData.min.toString()
                                : `${regularData.min}-${regularData.max}`
                            ) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
        </>
      )}

      {activeTab === 'duplicates' && (
        <div className="duplicates-section">
          {loadingDuplicates ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Carregando duplicados...</p>
            </div>
          ) : (
            <>
              <div className="duplicates-summary">
                <div className="summary-header">
                  <h3>Resumo de Duplicados</h3>
                  <button className="export-button" onClick={exportDuplicatesToPDF}>
                    📄 Exportar PDF
                  </button>
                </div>
                <p><strong>Total de grupos duplicados:</strong> {duplicateStats.totalGroups}</p>
                <p><strong>Total de registros repetidos:</strong> {duplicateStats.totalRecords}</p>
              </div>
              
              {duplicates.length > 0 ? (
                <div className="duplicates-table-container">
                  <table className="duplicates-table">
                    <thead>
                      <tr>
                        <th>Ordem</th>
                        <th>Número Precatório</th>
                        <th>Natureza</th>
                        <th>Orçamento</th>
                        <th>Prioridade</th>
                        <th>Valor Atualizado</th>
                        <th>Ente</th>
                        <th>Recebimento</th>
                        <th>Fonte</th>
                      </tr>
                    </thead>
                    <tbody>
                      {duplicates.map((dup, index) => {
                        // Check if this is the start of a new group
                        const isGroupStart = index === 0 || dup.numero_precatorio !== duplicates[index - 1].numero_precatorio;
                        const isGroupEnd = index === duplicates.length - 1 || dup.numero_precatorio !== duplicates[index + 1].numero_precatorio;
                        let className = dup.fonte.toLowerCase();
                        if (isGroupStart && isGroupEnd) {
                          className += ' single-group';
                        } else if (isGroupStart) {
                          className += ' group-start';
                        } else if (isGroupEnd) {
                          className += ' group-end';
                        } else {
                          className += ' group-middle';
                        }
                        
                        return (
                          <tr key={index} className={className}>
                            <td>{dup.ordem}</td>
                            <td>{dup.numero_precatorio}</td>
                            <td>{dup.natureza}</td>
                            <td>{dup.orcamento}</td>
                            <td>{dup.prioridade}</td>
                            <td>{formatCurrency(dup.valor_atualizado)}</td>
                            <td>{dup.ente}</td>
                            <td>{dup.recebimento}</td>
                            <td>
                              <span className={`fonte-badge ${dup.fonte.toLowerCase()}`}>
                                {dup.fonte}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-duplicates">
                  <p>Nenhum precatório duplicado encontrado.</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AMDashboard;